import { Deserializer } from "jsonapi-serializer";
import { CREATED, OK } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { OldPreset } from "../models/OldPreset";
import PresetCustomField, { PresetCustomFieldWrapper } from "../models/PresetCustomField";
import { APIConnector } from "./api/APIConnector";
import { convertToEndTime, convertToTime, getDays, getFormattedDate } from "./utilities/dateFormatting";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from "../contexts/translator";

export const apiConnector = new APIConnector();

const compileFilters = (profileId, listParameters) => {
    const profileFilter = {
        key: 'profile.id',
        label: 'Profile', // not displayed
        options: [{ label: profileId, value: profileId }],
        selected: [{ label: profileId, value: profileId }]
    };
    const actionSettingTypeFilter = {
        key: 'settingType.stSysname',
        label: 'Setting Type', // not displayed
        options: [{ label: 'M_*', value: 'M_*' }],
        selected: [{ label: 'M_*', value: 'M_*' }]
    };
    return getFilterClause(
        [...listParameters?.filters ?? [], profileFilter, actionSettingTypeFilter],
        listParameters?.searchTerm
    );
}

export const buildDeviceSchedulePreset = (apiPreset, translate: Translator): PresetCustomField => {
    let deviceTimePatternRecord;
    apiPreset['setting-datas'].filter(((settingData) => {
        if (settingData['time-pattern']) {
            const startTime = settingData['time-pattern']['tp-start-time'];
            deviceTimePatternRecord = {
                deviceTimePatternId: settingData['time-pattern']['id'],
                deviceTimePatternName: settingData['time-pattern']['tp-name'],
                endTime: (startTime ? convertToEndTime((settingData['time-pattern']['tp-start-time']).toString(), (settingData['time-pattern']['tp-duration']).toString()) : '--:--'),
                days: getDays(
                    settingData['time-pattern']['tp-monday'],  
                    settingData['time-pattern']['tp-tuesday'], 
                    settingData['time-pattern']['tp-wednesday'], 
                    settingData['time-pattern']['tp-thursday'], 
                    settingData['time-pattern']['tp-friday'],
                    settingData['time-pattern']['tp-saturday'], 
                    settingData['time-pattern']['tp-sunday'], 
                    settingData['time-pattern']['tp-public-holiday'], 
                    translate),
                startDate: settingData['time-pattern']['tp-start-date'] ? getFormattedDate(settingData['time-pattern']['tp-start-date']) : '----',
                startTime: settingData['time-pattern']['tp-start-time'] ? convertToTime(settingData['time-pattern']['tp-start-time']) : '--:--',
                endDate: settingData['time-pattern']['tp-end-date'] ? getFormattedDate(settingData['time-pattern']['tp-end-date']) : '----',
            }
        }
    }))

    return {
        ...deviceTimePatternRecord,
        id: apiPreset['id'],
        name: apiPreset['trn-name'],
        mode: apiPreset['setting-type']?.['trn-name'],
        settingTypeId: apiPreset['setting-type']?.['id'],
        sdist: apiPreset['s-dist'],
        profileId: apiPreset['profile']?.['id'],
        settingIdToCopy: apiPreset['setting-id-to-copy']
    };
};

export const getPresetDeviceSchedules = async (profileId: string, translate: Translator, listParameters?: ListQuery): Promise<PresetCustomFieldWrapper> => {
    try {
        const filter: string = compileFilters(profileId, listParameters);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[setting]=settingType,sDist,profile,settingDatas,trnName,settingIdToCopy';
        const settingTypeFields = 'fields[settingType]=trnName';
        const settingDataFields = 'fields[settingData]=sdValstring,timePattern';
        const timePatternFields = 'fields[timePattern]=tpName,tpStartDate,tpEndDate,tpStartTime,tpDuration,tpMonday,tpTuesday,tpWednesday,tpThursday,tpFriday,tpSaturday,tpSunday,tpPublicHoliday';
        const include = 'include=profile,settingType,settingDatas.timePattern';
        const url = `setting?${include}&${settingFields}&${settingTypeFields}&${settingDataFields}&${timePatternFields}${paginationString}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, response: any[]) => {
                if (error) {
                    return reject({ message: genericError });
                }

                const presetInputs: PresetCustomField[] = response.map(preset => buildDeviceSchedulePreset(preset, translate));
                return resolve({ rows: presetInputs, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
}

export const storeDeviceSchedulePreset = async (devicePresetSchedule) => {
    try {
        const newRecord = {
            type: "setting",
            attributes: {
                trnName: 'Door Mode Patterns',
                sDec: null,
                sDirection: 2,
                sDist: 'GLOBAL',
                settingIdToCopy: devicePresetSchedule.settingIdToCopy
            },
            relationships: {
                profileId: parseInt(devicePresetSchedule.profileId),
                settingTypeId: parseInt(devicePresetSchedule.settingTypeId),
                settingData: [{
                    eSysnameLookup: 'M_DEVICE_TIME_PATTERN',
                    sdValstring: devicePresetSchedule.deviceTimePatternId + '',
                    timePatternId: parseInt(devicePresetSchedule.deviceTimePatternId)
                },
                {
                    eSysnameLookup: 'M_REPORT',
                    sdValstring: '1'
                }
                ]
            }
        }

        const url = '';
        let settingResponse: Response;
         await apiConnector.sendData(url, true, newRecord, true).then(data => {
            settingResponse = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject({statusCode: settingResponse.status, message: genericError });
            })
        });

        return new Promise(async (resolve, reject) => {
            if (settingResponse) {
                if (settingResponse.status === OK
                    || settingResponse.status === CREATED) {
                    try {
                        const settingJson = await settingResponse.json()
                        return resolve({ id: settingJson['attributes']?.['id'], success: true })
                    }
                    catch (exception) {
                        return reject({ id: devicePresetSchedule.id, message: genericError, success: false });
                    }
                }
                // any status error
                if (settingResponse.body) {
                    // error comes back as html, cannot extract json, responding with generic error for now.
                    // const settingJson = await settingResponse.json()
                    // return reject({ id: newPreset.id, message: settingJson.errors, success: false });
                    if (settingResponse.statusText) {
                        // const jsonObj = JSON.parse(settingResponse.statusText);
                        // const errMsg = getConstraintViolation(jsonObj.errors[0].detail);
                        const content = await settingResponse.json();
                        return reject({ id: devicePresetSchedule.id, message: content.errors.map(error => getConstraintViolation(error.detail)), success: false });
                    }
                    return reject({ id: devicePresetSchedule.id, message: genericError, success: false });
                }
                return reject({ id: devicePresetSchedule.id, message: genericError, success: false });
            }
            return reject({ id: devicePresetSchedule.id, message: genericError, success: false });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
}

export const getModesNamesWithPagination = async (profileId: string, listParameters?: ListQuery): Promise<PresetCustomFieldWrapper> => {
    try {
        const filter: string = compileFilters(profileId, listParameters);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[setting]=settingType,sDist,profile';
        const settingTypeFields = 'fields[settingType]=trnName';
        const url = `setting?include=profile,settingType${paginationString}&${settingFields}&${settingTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        const EMERGENCY_MODE = '165';
        const LOCKDOWN_MODE = '168'

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject(processServerError(error));
                }

                const modes: any[] = data.map((setting): any => {
                    return {
                        id: setting['id'],
                        name: setting['setting-type']?.['trn-name'],
                        originalName: setting['setting-type']?.['trn-name'],
                        sdist: setting['s-dist'],
                        profileId: setting['profile']['id'],
                        settingIdToCopy: setting['id'],
                        settingTypeId: setting['setting-type']['id'],
                    };
                });

                modes.forEach((item, index) => {
                    if (item.id === EMERGENCY_MODE || item.id === LOCKDOWN_MODE) {
                        modes.splice(index, 1);
                    }
                });

                return resolve({ rows: modes, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const buildPreset = (apiPreset, apiSettingDatas?, apiElementNames?): any => {
    const newCustom = apiSettingDatas && apiElementNames
        ? apiElementNames.reduce((customFragment, elementName) => {
            const settingDatas = apiSettingDatas.find(settingData => settingData['element']['id'] === elementName['id']);
            customFragment[elementName['e-sysname']] = {
                id: elementName['id'],
                value: settingDatas?.['sd-valstring'] ?? '',
                settingDataId: settingDatas?.['id'] ?? '',
                ...(elementName['e-sysname'] === 'M_DEVICE_TIME_PATTERN' && {
                    deviceTimePatternId: settingDatas?.['sd-valstring'] ?? '',
                }),
            }

            return customFragment;
        }, {})
        : {}

    return {
        id: apiPreset['id'],
        name: apiPreset['trn-name'],
        type: apiPreset['setting-type']?.['trn-name'],
        settingTypeId: apiPreset['setting-type']?.['id'],
        custom: newCustom,
        sdist: apiPreset['s-dist'],
        profileId: apiPreset['profile']?.['id'],
        settingIdToCopy: apiPreset['setting-id-to-copy'],
        deviceTimePatternId: newCustom['M_DEVICE_TIME_PATTERN']['value']
    };
};

export const getDeviceSchedulePresetById = async (id: string): Promise<OldPreset> => {
    try {
        const url = `setting/${id}?include=settingType,settingDatas,profile`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            // return the id for element relationship to be used later.
            new Deserializer({
                element: {
                    valueForRelationship: (relationship) => {
                        return { id: relationship.id }
                    }
                }
            }).deserialize(json, async (error: any, apiPreset: any) => {
                if (error) {
                    return reject(error);
                }
                const presetTypeId = apiPreset['setting-type']?.['id'];

                const elementsUrl = `element?include=settingType&filter=settingType.id==${presetTypeId}`;
                const elementsResponse = await apiConnector.fetchData(elementsUrl);
                const elementsJson = await elementsResponse.json();

                new Deserializer().deserialize(elementsJson, async (error: any, apiElementNames: any[]) => {
                    if (error) {
                        return reject(error);
                    }
                    const preset: OldPreset = buildPreset(apiPreset, apiPreset['setting-datas'], apiElementNames);

                    return resolve(preset);
                });
            });
        })
    }
    catch (exception) {
        return new Promise((resolve, reject) => {
            reject(processServerError(exception));
        });
    }
};
